import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { BarSeriesOption, EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  getChartNumericFields,
  getCategoryValue,
  toNumericValue,
  buildCategoryAxis,
  buildValueAxis,
  getCartesianChartGrid,
  formatNumberShort,
  formatTooltipValue,
  getChartValueStats,
  formatTooltipStatsLine,
  getDisplayName,
  ECHARTS_OPTS,
  CHART_LABEL_FONT_SIZE,
  CHART_TEXT_COLOR,
  CHART_POSITIVE_COLOR,
  CHART_NEGATIVE_COLOR,
} from './chartConfig';

// Neutral color for absolute anchor bars (LY / CY totals).
const WATERFALL_TOTAL_COLOR = '#555A5F';

const VALUE_LABEL_DISTANCE = 8;
const VALUE_LABEL_MAX_BARS = 12;

export const WaterfallChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors, presentation }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const numericFields = getChartNumericFields(chart, data);
    const valueKey = numericFields.includes('value') ? 'value' : numericFields[0] ?? 'value';
    const seriesName = getDisplayName(chart, valueKey);
    const values = data.map((d) => toNumericValue(d[valueKey] ?? d.value ?? 0));

    // Cumulative stacked-bar waterfall: transparent floor + colored delta.
    // Rows with _waterfall_type === "total" are anchor bars (e.g. LY / CY totals)
    // that render as full absolute bars from zero and reset the running total.
    const baseVals: number[] = [];
    const increments: number[] = [];
    const isTotalBar: boolean[] = [];
    let running = 0;
    values.forEach((v, i) => {
      const nodeType = (data[i] as Record<string, unknown>)['_waterfall_type'] as string | undefined;
      const isTotal = nodeType === 'total';
      isTotalBar.push(isTotal);
      if (isTotal) {
        // Absolute anchor bar: starts at 0, height = |v|, resets cumulative.
        baseVals.push(0);
        increments.push(Math.abs(v));
        running = v;
      } else if (v >= 0) {
        baseVals.push(running);
        increments.push(v);
        running += v;
      } else {
        baseVals.push(running + v);
        increments.push(Math.abs(v));
        running += v;
      }
    });

    // Running total after each bar — used to position the dashed bridge connectors.
    const connectorLevels: number[] = [];
    let runningForConn = 0;
    values.forEach((v, i) => {
      if (isTotalBar[i]) {
        runningForConn = v;
      } else {
        runningForConn += v;
      }
      connectorLevels.push(runningForConn);
    });

    // y-axis min: always pin to yMin (= 0 in the normal case, or negative when the
    // running total dips below zero).  Without this, buildValueAxis auto-scales using
    // the raw delta values (e.g. -24.6M) and sets a negative y-axis floor, which
    // makes the y-axis zero line appear above the bottom of the chart and visually
    // distorts the relative heights of the total bars.
    const yMin = Math.min(0, ...baseVals);

    const showValueLabels = data.length <= VALUE_LABEL_MAX_BARS;

    const base = getBaseOption(colors);
    const gridLayout = getCartesianChartGrid(chart, data, [valueKey]);
    base.grid = {
      ...(base.grid as Record<string, unknown>),
      top: gridLayout.top,
      bottom: gridLayout.bottom,
    };

    const labels = data.map((d) => getCategoryValue(d as Record<string, unknown>));

    const xAxis = { ...(buildCategoryAxis(chart, data) as Record<string, unknown>) };
    const yAxis = { ...(buildValueAxis(chart, [valueKey]) as Record<string, unknown>) };
    // Always set min explicitly so the y-axis starts at 0 (or the negative floor).
    yAxis.min = yMin;

    if (presentation?.gridOverrides) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        ...(presentation.gridOverrides as Record<string, unknown>),
      };
    }

    if (presentation?.hideXAxisLabels) {
      xAxis.axisLabel = { ...(xAxis.axisLabel as Record<string, unknown>), show: false };
      xAxis.axisTick = { ...(xAxis.axisTick as Record<string, unknown>), show: false };
      xAxis.name = presentation.hideXAxisName ? '' : xAxis.name;
    }
    if (presentation?.hideYAxisLabels) {
      yAxis.axisLabel = { ...(yAxis.axisLabel as Record<string, unknown>), show: false };
      yAxis.axisTick = { ...(yAxis.axisTick as Record<string, unknown>), show: false };
      yAxis.name = presentation.hideYAxisName ? '' : yAxis.name;
    }

    // Dashed bridge connector lines between consecutive bars.
    // Each connector sits at connectorLevels[i] and spans from the right edge of
    // bar[i] to the left edge of bar[i+1], forming the staircase bridge visual.
    const connectorData = connectorLevels.slice(0, -1).map((level, i) => [
      { xAxis: labels[i], yAxis: level },
      { xAxis: labels[i + 1], yAxis: level },
    ]);

    const incrementSeries: BarSeriesOption = {
      name: seriesName,
      type: 'bar',
      stack: 'waterfall',
      barMaxWidth: 48,
      data: increments.map((v, i) => ({
        value: v,
        itemStyle: {
          color: isTotalBar[i] ? WATERFALL_TOTAL_COLOR : (values[i] >= 0 ? CHART_POSITIVE_COLOR : CHART_NEGATIVE_COLOR),
          borderRadius: isTotalBar[i] ? [4, 4, 4, 4] : (values[i] >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4]),
        },
        // Per-item label so position (top vs bottom) can vary without a
        // function callback on the series label (which TypeScript rejects).
        label: showValueLabels ? {
          show: true,
          position: (!isTotalBar[i] && values[i] < 0) ? 'bottom' : 'top',
          distance: VALUE_LABEL_DISTANCE,
          fontSize: CHART_LABEL_FONT_SIZE,
          color: CHART_TEXT_COLOR,
          formatter: () => {
            const val = values[i];
            if (val === 0) return '';
            if (isTotalBar[i]) return formatNumberShort(Math.abs(val));
            return `${val > 0 ? '+' : '−'}${formatNumberShort(Math.abs(val))}`;
          },
        } : { show: false },
      })),
      labelLayout: { hideOverlap: true },
      emphasis: { focus: 'series' },
      // Dashed connectors bridging consecutive bars at the running-total level.
      markLine: {
        symbol: ['none', 'none'],
        silent: true,
        animation: false,
        label: { show: false },
        lineStyle: {
          type: 'dashed',
          color: '#888888',
          width: 1,
          opacity: 0.55,
        },
        data: connectorData as unknown as [],
      },
    };

    const baseSeries: BarSeriesOption = {
      name: 'baseline',
      type: 'bar',
      stack: 'waterfall',
      barMaxWidth: 48,
      silent: true,
      data: baseVals,
      itemStyle: { color: 'transparent' },
      emphasis: { itemStyle: { color: 'transparent' } },
    };

    return {
      ...base,
      xAxis,
      yAxis,
      series: [baseSeries, incrementSeries],
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const idx = (items[0] as { dataIndex?: number })?.dataIndex ?? 0;
          const label = getCategoryValue(data[idx] as Record<string, unknown>);
          const v = values[idx];
          const isTotal = isTotalBar[idx];
          const color = isTotal ? WATERFALL_TOTAL_COLOR : (v >= 0 ? CHART_POSITIVE_COLOR : CHART_NEGATIVE_COLOR);
          let html = `<div style="margin-bottom:6px;font-size:${CHART_LABEL_FONT_SIZE}px;color:#F7F3EE">${label}</div>`;
          if (isTotal) {
            html += `<div style="display:flex;align-items:center;gap:8px;font-size:${CHART_LABEL_FONT_SIZE}px"><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color}"></span><span>${seriesName}</span><span style="margin-left:auto">${formatTooltipValue(Math.abs(v))}</span></div>`;
          } else {
            const sign = v > 0 ? '+' : v < 0 ? '−' : '';
            // Running total up to and including this delta
            let runningTotal = 0;
            for (let k = 0; k <= idx; k++) {
              runningTotal = isTotalBar[k] ? values[k] : runningTotal + values[k];
            }
            html += `<div style="display:flex;align-items:center;gap:8px;font-size:${CHART_LABEL_FONT_SIZE}px"><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color}"></span><span>${seriesName}</span><span style="margin-left:auto">${sign}${formatTooltipValue(Math.abs(v))}</span></div>`;
            html += `<div style="margin-top:4px;font-size:11px;color:#D9CFC4">Running total: ${formatTooltipValue(runningTotal)}</div>`;
          }
          const stats = getChartValueStats(data, [valueKey]);
          html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(247,243,238,.22);font-size:11px;color:#D9CFC4">${formatTooltipStatsLine(stats)}</div>`;
          return html;
        },
      },
      legend: { show: false },
    };
  }, [chart, colors, presentation]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
